import {
  ActionIcon,
  Alert,
  Button,
  Card,
  Group,
  Loader,
  Menu,
  NumberInput,
  Popover,
  Progress,
  RingProgress,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import {
  IconChevronLeft,
  IconChevronRight,
  IconCloudUp,
  IconDots,
  IconDroplet,
  IconPencil,
  IconPlus,
  IconScale,
  IconTrash,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { api, call, type DaySummary, type Entry, type Meal } from "../api/client";
import { EntryModal } from "../components/EntryModal";
import { SwipeToDelete } from "../components/SwipeToDelete";
import { MEAL_LABELS, MEALS, dayLabel, grams, kcal, shiftDay } from "../lib/format";
import { useToday } from "../lib/today";
import { formatWeight, useUnits, weightIn, weightToKg, weightUnit, type Units } from "../lib/units";
import { useLogActions, useSyncState } from "../sync/actions";
import { applyPending } from "../sync/overlay";

export function useDaySummary(day: string) {
  return useQuery({
    queryKey: ["summary", "day", day],
    queryFn: () => call(api.GET("/api/v1/summary/day/{day}", { params: { path: { day } } })),
  });
}

export function TodayPage() {
  const [params, setParams] = useSearchParams();
  const today = useToday();
  const day = params.get("day") ?? today;
  const summary = useDaySummary(day);
  const sync = useSyncState();
  // The server's day plus changes made on this device that it doesn't have yet.
  const data = useMemo(
    () => summary.data && applyPending(summary.data, sync.pending, sync.settled, summary.dataUpdatedAt),
    [summary.data, summary.dataUpdatedAt, sync.pending, sync.settled],
  );
  const pendingIds = useMemo(
    () =>
      new Set(
        sync.pending.flatMap((o) =>
          o.kind === "putEntry" ? [o.id] : o.kind === "createFoodAndLog" ? [o.entryId] : [],
        ),
      ),
    [sync.pending],
  );
  const [modal, setModal] = useState<{ meal: Meal; entry?: Entry } | null>(null);

  const goTo = (d: string) => setParams(d === today ? {} : { day: d });

  return (
    <Stack>
      <Group justify="space-between" wrap="nowrap">
        <ActionIcon variant="default" size="lg" onClick={() => goTo(shiftDay(day, -1))} aria-label="Previous day">
          <IconChevronLeft size={18} />
        </ActionIcon>
        <Stack gap={0} align="center">
          <Title order={3}>{dayLabel(day, today)}</Title>
          <TextInput
            type="date"
            size="xs"
            variant="unstyled"
            value={day}
            onChange={(e) => e.currentTarget.value && goTo(e.currentTarget.value)}
            aria-label="Pick a date"
          />
        </Stack>
        <ActionIcon variant="default" size="lg" onClick={() => goTo(shiftDay(day, 1))} aria-label="Next day">
          <IconChevronRight size={18} />
        </ActionIcon>
      </Group>

      {summary.isPending && <Loader />}
      {summary.isError && !data && (
        <Alert color="red" title="Couldn't load this day">
          {summary.error.message}
          {" "}Days you've opened before are saved on this device and work offline.
        </Alert>
      )}
      {data && (
        <>
          <Overview s={data} />
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            {MEALS.map((meal) => (
              <MealCard
                key={meal}
                meal={meal}
                s={data}
                pendingIds={pendingIds}
                onAdd={() => setModal({ meal })}
                onEdit={(entry) => setModal({ meal, entry })}
              />
            ))}
          </SimpleGrid>
          <Metrics s={data} />
        </>
      )}

      <EntryModal
        opened={modal !== null}
        onClose={() => setModal(null)}
        day={day}
        meal={modal?.meal ?? "breakfast"}
        entry={modal?.entry}
      />
    </Stack>
  );
}

function Overview({ s }: { s: DaySummary }) {
  const goal = s.goal?.kcal;
  const eaten = s.totals.kcal;
  const over = goal != null && eaten > goal;
  const pct = goal ? Math.min(100, (eaten / goal) * 100) : 0;

  const macros = [
    { label: "Protein", value: s.totals.protein_g, goal: s.goal?.protein_g, color: "blue" },
    { label: "Carbs", value: s.totals.carbs_g, goal: s.goal?.carbs_g, color: "yellow" },
    { label: "Fat", value: s.totals.fat_g, goal: s.goal?.fat_g, color: "grape" },
  ];

  return (
    <Card withBorder>
      <Group align="center" justify="space-between" wrap="wrap" gap="lg">
        <Group wrap="nowrap">
          <RingProgress
            size={120}
            thickness={12}
            roundCaps
            sections={[{ value: pct, color: over ? "red" : "teal" }]}
            label={
              <Stack gap={0} align="center">
                <Text fw={700} size="lg">
                  {kcal(eaten)}
                </Text>
                <Text size="xs" c="dimmed">
                  kcal eaten
                </Text>
              </Stack>
            }
          />
          <Stack gap={4}>
            {goal != null ? (
              <>
                <Text size="sm">
                  Goal <b>{kcal(goal)}</b>
                </Text>
                <Text size="sm" c={over ? "red" : undefined} data-testid="remaining">
                  {over ? "Over by" : "Remaining"} <b>{kcal(Math.abs(s.remaining_kcal ?? 0))}</b>
                </Text>
              </>
            ) : (
              <Text size="sm" c="dimmed">
                No goal set yet
              </Text>
            )}
          </Stack>
        </Group>
        <Stack gap="xs" style={{ flex: 1, minWidth: 200 }}>
          {macros.map((m) => (
            <div key={m.label}>
              <Group justify="space-between">
                <Text size="xs">{m.label}</Text>
                <Text size="xs" c="dimmed">
                  {grams(m.value)}
                  {m.goal != null && ` / ${grams(m.goal)}`}
                </Text>
              </Group>
              <Progress
                value={m.goal ? Math.min(100, (m.value / m.goal) * 100) : 0}
                color={m.goal != null && m.value > m.goal ? "red" : m.color}
                size="sm"
              />
            </div>
          ))}
        </Stack>
      </Group>
    </Card>
  );
}

function MealCard({
  meal,
  s,
  pendingIds,
  onAdd,
  onEdit,
}: {
  meal: Meal;
  s: DaySummary;
  pendingIds: Set<string>;
  onAdd: () => void;
  onEdit: (e: Entry) => void;
}) {
  const actions = useLogActions();
  const failed = (title: string) => (err: unknown) =>
    notifications.show({ color: "red", title, message: (err as Error).message });
  const remove = (e: Entry) => {
    // Saved on the device first, so this works offline; Undo queues it back.
    actions.deleteEntry(e).catch(failed("Couldn't delete"));
    notifications.show({
      id: `deleted-${e.id}`,
      autoClose: 6000,
      message: (
        <Group justify="space-between" wrap="nowrap">
          <Text size="sm" truncate>
            Deleted {e.name}
          </Text>
          <Button
            size="compact-sm"
            variant="light"
            onClick={() => {
              notifications.hide(`deleted-${e.id}`);
              actions.restoreEntry(e).catch(failed("Couldn't undo"));
            }}
          >
            Undo
          </Button>
        </Group>
      ),
    });
  };
  const m = s.meals[meal];
  return (
    <Card withBorder padding="sm" data-testid={`meal-${meal}`}>
      <Group justify="space-between" mb="xs">
        <Text fw={600}>{MEAL_LABELS[meal]}</Text>
        <Text size="sm" c="dimmed">
          {kcal(m?.totals.kcal ?? 0)} kcal
        </Text>
      </Group>
      <Stack gap={6}>
        {m?.entries.map((e) => (
          <SwipeToDelete key={e.id} onDelete={() => remove(e)}>
            <Group justify="space-between" wrap="nowrap" gap="xs" py={2}>
              <div style={{ minWidth: 0 }}>
                <Text size="sm" truncate>
                  {e.name}
                </Text>
                <Text size="xs" c="dimmed" truncate>
                  {[e.amount, `P ${grams(e.protein_g)} · C ${grams(e.carbs_g)} · F ${grams(e.fat_g)}`]
                    .filter(Boolean)
                    .join(" · ")}
                </Text>
              </div>
              <Group gap={4} wrap="nowrap">
                {pendingIds.has(e.id) && (
                  <IconCloudUp size={14} color="var(--mantine-color-yellow-6)" aria-label="Not synced yet" />
                )}
                <Text size="sm">{kcal(e.kcal)}</Text>
                <Menu position="bottom-end" withinPortal>
                  <Menu.Target>
                    <ActionIcon variant="subtle" color="gray" aria-label={`Options for ${e.name}`}>
                      <IconDots size={16} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => onEdit(e)}>
                      Edit
                    </Menu.Item>
                    <Menu.Item
                      color="red"
                      leftSection={<IconTrash size={14} />}
                      onClick={() => remove(e)}
                    >
                      Delete
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
            </Group>
          </SwipeToDelete>
        ))}
        <Button variant="light" size="xs" leftSection={<IconPlus size={14} />} onClick={onAdd}>
          Add food
        </Button>
      </Stack>
    </Card>
  );
}

const METRIC_LABELS: Record<string, [string, (v: number, units: Units) => string]> = {
  steps: ["Steps", (v) => v.toLocaleString()],
  active_kcal: ["Active", (v) => `${kcal(v)} kcal`],
  exercise_minutes: ["Exercise", (v) => `${v} min`],
  sleep_minutes: ["Sleep", (v) => `${Math.floor(v / 60)}h ${Math.round(v % 60)}m`],
};

function Metrics({ s }: { s: DaySummary }) {
  const units = useUnits();
  const actions = useLogActions();
  const addWater = (delta: number) =>
    actions.addWater(s.date, delta).catch((err: Error) =>
      notifications.show({ color: "red", title: "Couldn't save", message: err.message }),
    );
  const shown = Object.entries(METRIC_LABELS).filter(([k]) => s.metrics[k as keyof typeof s.metrics] != null);
  return (
    <Card withBorder padding="sm">
      <Group justify="space-between" wrap="wrap">
        <Group gap="xs">
          <IconDroplet size={18} color="var(--mantine-color-blue-5)" />
          <Text size="sm" data-testid="water">
            Water <b>{(s.metrics.water_ml ?? 0).toLocaleString()} ml</b>
          </Text>
          <Button size="compact-xs" variant="light" onClick={() => addWater(250)}>
            +250 ml
          </Button>
          <Button size="compact-xs" variant="subtle" color="gray" onClick={() => addWater(-250)} disabled={!s.metrics.water_ml}>
            −250
          </Button>
        </Group>
        <Weight date={s.date} kg={s.metrics.weight_kg} />
        <Group gap="md">
          {shown.map(([key, [label, fmt]]) => (
            <Text key={key} size="sm">
              {label} <b>{fmt(s.metrics[key as keyof typeof s.metrics]!, units)}</b>
            </Text>
          ))}
        </Group>
      </Group>
    </Card>
  );
}

// Same sanity limits as the goal calculator's weight.
const MIN_KG = 20;
const MAX_KG = 500;

function Weight({ date, kg }: { date: string; kg: number | undefined }) {
  const units = useUnits();
  const actions = useLogActions();
  const [opened, setOpened] = useState(false);
  const [value, setValue] = useState<string | number>("");
  const unit = weightUnit(units);
  const asKg = typeof value === "number" ? weightToKg(value, units) : null;
  const valid = asKg != null && asKg >= MIN_KG && asKg <= MAX_KG;

  const open = () => {
    setValue(kg != null ? weightIn(kg, units) : "");
    setOpened(true);
  };
  const save = (next: number | null) => {
    setOpened(false);
    actions.setWeight(date, next).catch((err: Error) =>
      notifications.show({ color: "red", title: "Couldn't save", message: err.message }),
    );
  };

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom" withArrow trapFocus>
      <Popover.Target>
        <Group gap="xs" data-testid="weight">
          <IconScale size={18} color="var(--mantine-color-grape-5)" />
          {kg != null ? (
            <Button size="compact-xs" variant="subtle" color="dark" onClick={open} aria-label="Edit weight">
              <Text size="sm" span>
                Weight <b>{formatWeight(kg, units)}</b>
              </Text>
            </Button>
          ) : (
            <Button size="compact-xs" variant="light" color="grape" onClick={open}>
              Log weight
            </Button>
          )}
        </Group>
      </Popover.Target>
      <Popover.Dropdown>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) save(asKg);
          }}
        >
          <Stack gap="xs">
            <NumberInput
              label={`Weight (${unit})`}
              value={value}
              onChange={setValue}
              decimalScale={1}
              min={0}
              hideControls
              inputMode="decimal"
              data-autofocus
              w={160}
              error={value !== "" && !valid ? "Doesn't look right" : undefined}
            />
            <Group gap="xs" justify="space-between">
              {kg != null ? (
                <Button size="xs" variant="subtle" color="red" onClick={() => save(null)}>
                  Remove
                </Button>
              ) : (
                <span />
              )}
              <Button size="xs" type="submit" disabled={!valid}>
                Save
              </Button>
            </Group>
          </Stack>
        </form>
      </Popover.Dropdown>
    </Popover>
  );
}
